import { type QueryHookOptions, type QueryResult, useQuery } from '@apollo/client'
import {
  type GetSearchResultsPageByUrlQuery,
  type PaginationInputV2,
  type SearchResultsByUrlInput,
  GetPendingSearchInputDocument,
  GetSearchResultsPageByUrlDocument,
} from '@kijiji/generated/graphql-types'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { getSrpComposedUrl } from '@/domain/srp/getSrpComposedUrl'
import { getPaginationFromUrl } from '@/domain/srp/pagination/getPaginationFromUrl'
import { useSearchLoadingState } from '@/hooks/srp/useSearchLoadingState'
import { currentSrpUrl } from '@/lib/apollo/currentSrpUrl'
import { RemoteParamKeys, useExperiment, useToggle } from '@/lib/firebase/hooks'
import { createExperimentsContext } from '@/lib/firebase/utils/createExperimentsContext'
import { convertFromToggleToExperiment } from '@/lib/firebase/utils/createToggleExperiment'

export type SearchResultsPageByUrlInput = {
  searchResultsByUrlInput: SearchResultsByUrlInput
  pagination: PaginationInputV2
}

type Result<TQuery extends GetSearchResultsPageByUrlQuery> = Omit<
  QueryResult<TQuery, SearchResultsPageByUrlInput>,
  'data'
> & {
  data?: TQuery['searchResultsPageByUrl']
  setLoadingStates: (params: { filters?: boolean; results?: boolean }) => void
  loadingResults: boolean
  loadingFilters: boolean
}

export function useGetSearchResultsData(
  options?: QueryHookOptions<GetSearchResultsPageByUrlQuery, SearchResultsPageByUrlInput>
): Result<GetSearchResultsPageByUrlQuery> {
  const { query } = useRouter()
  const srpUrl = getSrpComposedUrl(query)
  const srpUrlString = srpUrl.href
  const previousSrpUrl = currentSrpUrl()

  const { loadingResults, loadingFilters, setLoadingStates } = useSearchLoadingState()
  const { fetchPolicy, ...otherOptions } = options || {}

  const pagination = getPaginationFromUrl(srpUrl.href)

  const bestMatchExperiment = useExperiment(RemoteParamKeys.BEST_MATCH_ALGORITHM_TEST)
  const deduplicationToggle = useToggle(RemoteParamKeys.SRP_DEDUPLICATION_TEST_TOGGLE)
  const sellerResponsivenessExperiment = useExperiment(RemoteParamKeys.SELLER_RESPONSIVENESS_TEST)
  const imageDownBoostingExperiment = useExperiment(RemoteParamKeys.IMAGELESS_DOWN_BOOSTING_TEST)
  const menuRealEstateQuickLinkExperiment = useExperiment(
    RemoteParamKeys.MENU_REAL_ESTATE_QUICK_LINK_TEST
  )

  const experimentsContext = createExperimentsContext([
    bestMatchExperiment,
    sellerResponsivenessExperiment,
    imageDownBoostingExperiment,
    menuRealEstateQuickLinkExperiment,
    convertFromToggleToExperiment(deduplicationToggle),
  ])

  /**
   * It assumes the data has already been fetched and cached.
   * It does not make a network request (fetchPolicy: 'cache-only').
   * It should also respect the URL pagination to retrieve the correct cached value.
   * */
  const {
    data: newData,
    previousData,
    loading: newDataLoading,
    called,
    error,
    client,
    ...rest
  } = useQuery<GetSearchResultsPageByUrlQuery, SearchResultsPageByUrlInput>(
    GetSearchResultsPageByUrlDocument,
    {
      context: {
        ...(options?.context || {}),
        ...(Object.keys(experimentsContext).length > 0 ? { experiments: experimentsContext } : {}),
      },
      fetchPolicy: fetchPolicy || 'cache-only',
      variables: {
        searchResultsByUrlInput: {
          url: srpUrl.href,
          pagination,
        },
        pagination,
      },
      ...otherOptions,
    }
  )

  useEffect(() => {
    // TODO: there must be a better way to handle knowing when the new data is loaded
    if (!newDataLoading && !error && !!newData && srpUrlString !== previousSrpUrl) {
      client.writeQuery({
        query: GetPendingSearchInputDocument,
        data: {
          srp: { pendingSearchInput: null },
        },
      })

      // loading is done we need to save the url to state to use as next load fill
      currentSrpUrl(srpUrlString)
      setLoadingStates(false)
    }
  }, [newData, error, newDataLoading, srpUrlString, previousSrpUrl, setLoadingStates, client])
  /**
   * If the main/new data is empty, grab from preview data to prevent layout shift
   */
  const newOrPreviousData = newData || previousData

  return {
    setLoadingStates,
    loading: newDataLoading,
    loadingResults,
    loadingFilters,
    error,
    called,
    client,
    data: newOrPreviousData?.searchResultsPageByUrl || undefined,
    ...rest,
  }
}
